// @flow
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { FormattedMessage, injectIntl, type IntlShape } from 'react-intl';
import { Field, reduxForm, type FormProps } from 'redux-form';

import {
  createDirectoryServiceDescription,
  deleteDirectoryServiceDescription,
  getAdminProperties,
  updateDirectoryServiceDescription,
} from 'state/commands/adminPropertiesCommands';
import {
  getDirectoryServiceDescription,
  getIsCommunicatingWithDirectoryService,
} from 'state/selectors';
import type { Dispatch, DirectoryServiceDescription } from 'state/types';
import { ADMIN_PROPERTIES } from 'util/AdminProperties';
import localizedStrings from 'util/localizedStrings';
import { bindActionCreatorsForm } from 'util/safeDispatch';
import LabeledField from 'view/Forms/LabeledField';
import CheckRadio from 'view/Forms/BulmaCheckRadio';

type Props = {
  createDirectoryServiceDescription: typeof createDirectoryServiceDescription,
  deleteDirectoryServiceDescription: typeof deleteDirectoryServiceDescription,
  directoryServiceDescription: DirectoryServiceDescription,
  getAdminProperties: typeof getAdminProperties,
  intl: IntlShape,
  isCommunicatingWithDirectoryService: boolean,
  serviceType: string,
  updateDirectoryServiceDescription: typeof updateDirectoryServiceDescription,
} & FormProps;

class DirectoryServiceDescriptionPage extends Component<Props> {
  onClickDelete: Function;
  submit: Function;

  constructor() {
    super();
    this.onClickDelete = this.onClickDelete.bind(this);
    this.submit = this.submit.bind(this);
  }

  componentDidMount() {
    this.props.getAdminProperties([
      ADMIN_PROPERTIES.directoryServiceDescription,
    ]);
  }

  submit(values) {
    const {
      createDirectoryServiceDescription,
      updateDirectoryServiceDescription,
    } = this.props;
    const desc: DirectoryServiceDescription = {
      ...values,
      cacheCredentials: true,
      deleteMissingUsersOnUpdate: true,
      serviceType: 'ActiveDirectory',
      updateInterval: 1440,
    };
    if (values.uuid) {
      updateDirectoryServiceDescription(desc);
    } else {
      createDirectoryServiceDescription(desc);
    }
  }

  onClickDelete() {
    const {
      deleteDirectoryServiceDescription,
      directoryServiceDescription,
    } = this.props;
    directoryServiceDescription &&
      directoryServiceDescription.uuid &&
      deleteDirectoryServiceDescription(directoryServiceDescription.uuid);
  }

  componentWillReceiveProps(nextProps) {
    if (
      this.props.directoryServiceDescription !==
      nextProps.directoryServiceDescription
    ) {
      this.initializeFields(nextProps.directoryServiceDescription);
    }
  }

  initializeFields(directoryServiceDescription: DirectoryServiceDescription) {
    this.props.initialize(directoryServiceDescription);
  }

  render() {
    const {
      directoryServiceDescription,
      handleSubmit,
      intl: { formatMessage },
      invalid,
      isCommunicatingWithDirectoryService,
      pristine,
      reset,
    } = this.props;
    const hasUuid =
      directoryServiceDescription && directoryServiceDescription.uuid;
    return (
      <div
        className="page page-contents-textured portfolio-form-container"
        data-test-name="directory-service-description-page"
      >
        <div className="box">
          <form onSubmit={handleSubmit(this.submit)}>
            <div className="title is-4">
              <FormattedMessage
                {...localizedStrings.DIRECTORY_SERVICE_CONFIG_TITLE}
              />
            </div>
            <LabeledField
              labelClassName="is-medium"
              label={formatMessage(localizedStrings.HOSTNAME)}
            >
              <Field
                className={`input is-medium ${hasUuid ? 'is-static' : ''}`}
                disabled={hasUuid}
                component="input"
                id="hostname"
                name="hostname"
                style={{ width: '450px' }}
              />
            </LabeledField>
            <LabeledField label={formatMessage(localizedStrings.PORT)}>
              <Field
                className="input dir-svc-desc-form-field-small-width"
                component="input"
                id="port"
                max={65535}
                min={0}
                name="port"
                type="number"
              />
              <p className="help">
                <FormattedMessage {...localizedStrings.DEFAULT_PORT_NON_SSL} />{' '}
                389
              </p>
              <p className="help">
                <FormattedMessage {...localizedStrings.DEFAULT_PORT_SSL} /> 636
              </p>
            </LabeledField>

            <div className="vertical-spacer-large" />

            <LabeledField label={formatMessage(localizedStrings.USE_SSL)}>
              <Field
                component={CheckRadio}
                id="useLdaps"
                name="useLdaps"
                type="checkbox"
              />
            </LabeledField>

            <LabeledField
              label={formatMessage(localizedStrings.LDAP_BIND_USERNAME)}
            >
              <Field
                className="input dir-svc-desc-form-field-medium-width"
                component="input"
                id="loginDN"
                name="loginDN"
              />
            </LabeledField>
            <LabeledField label={formatMessage(localizedStrings.PASSWORD)}>
              <Field
                autoComplete="new-password"
                className="input dir-svc-desc-form-field-medium-width"
                component="input"
                id="credential"
                name="credential"
                type="password"
              />
            </LabeledField>

            <div className="vertical-spacer-large" />

            <LabeledField label={formatMessage(localizedStrings.SYNC_DAILY)}>
              <Field
                component={CheckRadio}
                id="autoUpdate"
                name="autoUpdate"
                type="checkbox"
              />
            </LabeledField>

            <div className="vertical-spacer-large" />

            <div
              className="dir-svc-desc-form-button-group"
              style={{ maxWidth: '500px' }}
            >
              <div className="control">
                <button
                  className="button is-dark is-outlined"
                  disabled={!hasUuid || isCommunicatingWithDirectoryService}
                  onClick={this.onClickDelete}
                  type="button"
                >
                  <FormattedMessage
                    {...localizedStrings.REMOVE_DIRECTORY_SERVICE}
                  />
                </button>
              </div>
              <div className="field is-grouped is-pulled-right">
                <div className="control">
                  <button
                    className="button is-dark is-outlined"
                    disabled={pristine || isCommunicatingWithDirectoryService}
                    onClick={reset}
                    type="button"
                  >
                    <FormattedMessage {...localizedStrings.REVERT} />
                  </button>
                </div>
                <div className="control">
                  <button
                    className={`button is-primary ${
                      isCommunicatingWithDirectoryService ? 'is-loading' : ''
                    }`}
                    type="submit"
                    disabled={
                      pristine | invalid || isCommunicatingWithDirectoryService
                    }
                  >
                    <FormattedMessage {...localizedStrings.APPLY} />
                  </button>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
    );
  }
}

function validate(data: Object, props: Props) {
  const errors = {};
  const maxFieldLen = 255;
  if (!data.hostname) {
    errors.hostname = 'Required';
  }
  if (!data.port) {
    errors.port = 'Required';
  }
  if (data.hostname && data.hostname.length > maxFieldLen) {
    errors.hostname = 'Invalid';
  }
  if (data.loginDN && data.loginDN.length > maxFieldLen) {
    errors.loginDN = 'Invalid';
  }
  if (data.credential && data.credential.length > maxFieldLen) {
    errors.credential = 'Invalid';
  }
  return errors;
}

const FORM_NAME = 'Directory Service Description';

const form = reduxForm({
  destroyOnUnmount: false,
  form: FORM_NAME,
  validate,
})(injectIntl(DirectoryServiceDescriptionPage));

export default connect(
  state => ({
    directoryServiceDescription: getDirectoryServiceDescription(state),
    isCommunicatingWithDirectoryService: getIsCommunicatingWithDirectoryService(
      state
    ),
  }),
  (dispatch: Dispatch) =>
    bindActionCreatorsForm({
      createDirectoryServiceDescription,
      deleteDirectoryServiceDescription,
      getAdminProperties,
      updateDirectoryServiceDescription,
    })
)(form);



// WEBPACK FOOTER //
// ./src/view/Pages/DirectoryServicesPage/DirectoryServiceDescriptionPage.js